import { gsap } from 'gsap'
import { deepMerge } from 'js/utils/Utils'
import Common from 'js/_core/Common'

const defaults = {
  instanceName: 'headerManager',
  $names: {
    header: '[data-header]',
    headerBG: '[data-header-bg]',
    headerLogo: '[data-header-logo]',
    headerNav: '[data-header-nav]',
    headerBtnMenu: '[data-header-btn-menu]',
    firstSection: '[data-first-section]',
  },
}

export default class HeaderManager {
  $contentRoot = document
  $header = null
  $headerBG = null
  $headerLogo = null
  $headerNav = null
  $headerBtnMenu = null
  $headerBtnMenuChild = null
  $firstSection = null

  headerHeight = 0
  firstSectionHeight = 0

  isMenuOpened = false
  isInFirstSection = true
  outFirstSectionPos = 0

  prevScroll = 0
  currentScroll = 0

  isPageHome = false

  constructor(options = {}) {
    deepMerge(this, defaults, options)
  }

  init() {
    this._set$()
    this.initialStyles()
    this._addHandlers()
    this._addEventListeners()
  }

  destroy() {
    this._removeHandlers()
    this._removeEventListeners()
  }

  _set$() {
    this.$header = document.querySelector(this.$names.header)
    this.$headerBG = document.querySelector(this.$names.headerBG)
    this.$headerLogo = document.querySelector(this.$names.headerLogo)
    this.$headerNav = document.querySelector(this.$names.headerNav)
    this.$headerBtnMenu = document.querySelector(this.$names.headerBtnMenu)
    if (this.$headerBtnMenu) {
      this.$headerBtnMenuChild = this.$headerBtnMenu.querySelector('button')
    }
  }

  _addHandlers() {
    if (Common.raf) {
      Common.raf.addHandler(() => {
        this.onRaf()
      }, this.instanceName)
    }

    if (Common.resize) {
      Common.resize.addHandler(() => {
        this.onResize()
      }, this.instanceName)
    }
  }

  _removeHandlers() {
    if (Common.raf) {
      Common.raf.removeHandler(this.instanceName)
    }

    if (Common.resize) {
      Common.resize.removeHandler(this.instanceName)
    }
  }

  _addEventListeners() {
    if (this.$headerBtnMenu) {
      this.$headerBtnMenu.addEventListener('click', this._onClickMenuBtn)
    }
  }

  _removeEventListeners() {
    if (this.$headerBtnMenu) {
      this.$headerBtnMenu.removeEventListener('click', this._onClickMenuBtn)
    }
  }

  initialStyles() {
    if (this.$headerLogo) {
      if (document.activeElement === this.$headerLogo) {
        this.$headerLogo.blur()
      }

      this.$headerLogo.setAttribute('tabindex', '-1')
      this.$headerLogo.setAttribute('aria-hidden', 'true')

      gsap.to(this.$headerLogo, {
        opacity: 0,
        pointerEvents: 'none',
      })
    }

    if (!Common.detection.isMobileLayout) {
      if (this.$headerBtnMenu) {
        gsap.set(this.$headerBtnMenu, {
          pointerEvents: 'none',
          opacity: 0,
        })

        if (this.$headerBtnMenuChild) {
          if (document.activeElement === this.$headerBtnMenuChild) {
            this.$headerBtnMenuChild.blur()
          }
          this.$headerBtnMenuChild.setAttribute('aria-expanded', 'false')
          this.$headerBtnMenuChild.setAttribute('tabindex', '-1')
          this.$headerBtnMenuChild.setAttribute('aria-hidden', 'true')
        } else {
          if (document.activeElement === this.$headerBtnMenu) {
            this.$headerBtnMenu.blur()
          }
          this.$headerBtnMenu.setAttribute('aria-expanded', 'false')
          this.$headerBtnMenu.setAttribute('tabindex', '-1')
          this.$headerBtnMenu.setAttribute('aria-hidden', 'true')
        }
      }
    }
  }

  pageEnter($content, id = '') {
    if (id === 'Home') {
      this.isPageHome = true
      this.hideLogo()
    } else {
      this.isPageHome = false
      this.showLogo()
    }

    this.hideBG()

    this.$contentRoot = $content || document
    this.$firstSection = this.$contentRoot.querySelector(this.$names.firstSection)
    this._setSizes()
  }

  pageLeave() {
    this.isMenuOpened = false
  }

  _setSizes() {
    this.headerHeight = this.$header ? this.$header.getBoundingClientRect().height : 0
    this.firstSectionHeight = this.$firstSection ? this.$firstSection.getBoundingClientRect().height : 0

    this.outFirstSectionPos = this.firstSectionHeight - this.headerHeight * 0.5
  }

  checkPos() {
    let isInFirstSection = true

    this.currentScroll = Common.scroll.currentScroll
    isInFirstSection = this.currentScroll < this.outFirstSectionPos

    if (Common.spMenu && Common.spMenu.isMenuOpened) {
      isInFirstSection = false
    }

    if (!this.isPageHome) {
      if (this.currentScroll > 50) {
        isInFirstSection = false
      } else {
        isInFirstSection = true
      }
    }

    if (isInFirstSection) {
      if (!this.isInFirstSection) {
        this.isInFirstSection = true
        this.inFirstSection()
      }
    }

    if (!isInFirstSection) {
      if (this.isInFirstSection) {
        this.isInFirstSection = false
        this.outFirstSection()
      }

      if (!Common.detection.isMobileLayout) {
        if (Math.abs(this.prevScroll - this.currentScroll) > 5) {
          if (this.isMenuOpened) {
            this.closeMenu()
            this.hideBG()
          }
        }
      }
    }

    this.prevScroll = this.currentScroll
  }

  inFirstSection() {
    if (!Common.detection.isMobileLayout) {
      this.showMenu()

      if (this.$headerBtnMenu) {
        if (this.$headerBtnMenuChild) {
          this.$headerBtnMenuChild.setAttribute('tabindex', '-1')
          this.$headerBtnMenuChild.setAttribute('aria-hidden', 'true')
          this.$headerBtnMenuChild.setAttribute('aria-expanded', 'false')
        } else {
          this.$headerBtnMenu.setAttribute('tabindex', '-1')
          this.$headerBtnMenu.setAttribute('aria-hidden', 'true')
          this.$headerBtnMenu.setAttribute('aria-expanded', 'false')
        }

        gsap.to(this.$headerBtnMenu, {
          opacity: 0,
          pointerEvents: 'none',
        })
      }
    }

    if (Common.spMenu) {
      if (this.isPageHome && Common.spMenu && !Common.spMenu.isMenuOpened) {
        this.hideLogo()
      }
    } else {
      if (this.isPageHome) {
        this.hideLogo()
      }
    }

    if (this.isPageHome && Common.spMenu && !Common.spMenu.isMenuOpened) {
      this.hideLogo()
    }
  }

  outFirstSection() {
    if (!Common.detection.isMobileLayout) {
      this.closeMenu()
      this.hideBG()

      if (this.$headerBtnMenu) {
        if (this.$headerBtnMenuChild) {
          this.$headerBtnMenuChild.setAttribute('tabindex', '0')
          this.$headerBtnMenuChild.setAttribute('aria-hidden', 'false')
        } else {
          this.$headerBtnMenu.setAttribute('tabindex', '0')
          this.$headerBtnMenu.setAttribute('aria-hidden', 'false')
        }

        gsap.to(this.$headerBtnMenu, {
          opacity: 1,
          pointerEvents: 'auto',
        })
      }
    }

    if (this.isPageHome) {
      this.showLogo()
    }
  }

  showMenu() {
    if (this.$headerNav) {
      if (!Common.detection.isMobileLayout) {
        this.$headerNav.setAttribute('aria-hidden', 'false')

        gsap.killTweensOf(this.$headerNav)
        gsap.to(this.$headerNav, {
          y: 0,
          rotate: 0.001,
          force3D: false,
          delay: 0.025,
          duration: 1,
          pointerEvents: 'auto',
          ease: 'expo.out',
        })

        gsap.killTweensOf(this.$headerBtnMenu)
        gsap.to(this.$headerBtnMenu, {
          opacity: 0,
          pointerEvents: 'none',
        })
      } else {
        // gsap.killTweensOf(this.$headerNav)
        // gsap.to(this.$headerNav, {
        //   opacity: 1,
        //   duration: 0.5,
        //   pointerEvents: 'auto',
        // })
      }
    }
  }

  hideMenu() {
    if (this.$headerNav) {
      if (!Common.detection.isMobileLayout) {
        gsap.killTweensOf(this.$headerNav)
        gsap.to(this.$headerNav, {
          y: '-100%',
          rotate: 0.001,
          force3D: false,
          duration: 1,
          pointerEvents: 'none',
          ease: 'expo.out',
        })

        gsap.killTweensOf(this.$headerBtnMenu)
        gsap.to(this.$headerBtnMenu, {
          opacity: 1,
          pointerEvents: 'auto',
        })
      } else {
        // gsap.killTweensOf(this.$headerNav)
        // gsap.to(this.$headerNav, {
        //   opacity: 0,
        //   duration: 0.5,
        //   pointerEvents: 'auto',
        // })
      }
    }
  }

  showLogo() {
    if (this.$headerLogo) {
      this.$headerLogo.setAttribute('tabindex', '0')
      this.$headerLogo.setAttribute('aria-hidden', 'false')

      gsap.to(this.$headerLogo, {
        opacity: 1,
        pointerEvents: 'auto',
      })
    }
  }

  hideLogo() {
    if (Common.spMenu && Common.spMenu.isMenuOpened) return

    if (this.$headerLogo) {
      if (document.activeElement === this.$headerLogo) {
        this.$headerLogo.blur()
      }

      this.$headerLogo.setAttribute('tabindex', '-1')
      this.$headerLogo.setAttribute('aria-hidden', 'true')

      gsap.to(this.$headerLogo, {
        opacity: 0,
        pointerEvents: 'none',
      })
    }
  }

  openMenu() {
    this.isMenuOpened = true

    if (this.$headerBtnMenuChild) {
      this.$headerBtnMenuChild.setAttribute('aria-expanded', 'true')
    } else {
      this.$headerBtnMenu.setAttribute('aria-expanded', 'true')
    }

    this.showMenu()
  }

  closeMenu() {
    this.isMenuOpened = false

    if (this.$headerBtnMenuChild) {
      this.$headerBtnMenuChild.setAttribute('aria-expanded', 'false')
    } else {
      this.$headerBtnMenu.setAttribute('aria-expanded', 'false')
    }
    this.hideMenu()
    if (Common.gNavDropdownController) {
      Common.gNavDropdownController.closeAllDropdowns()
    }
  }

  showBG() {
    if (this.$headerBG) {
      gsap.killTweensOf(this.$headerBG)
      gsap.to(this.$headerBG, {
        y: '0%',
        rotate: 0.001,
        force3D: false,
        duration: 1,
        ease: 'expo.out',
      })
    }
  }

  hideBG() {
    if (this.$headerBG) {
      gsap.killTweensOf(this.$headerBG)
      gsap.to(this.$headerBG, {
        y: '-100%',
        rotate: 0.001,
        force3D: false,
        duration: 1,
        ease: 'expo.out',
      })
    }
  }

  _onClickMenuBtn = () => {
    if (!Common.detection.isMobileLayout) {
      if (this.isMenuOpened) {
        this.closeMenu()
        this.hideBG()
      } else {
        this.openMenu()
        this.showBG()
      }
    }
  }

  onResize() {
    this._setSizes()
  }

  onRaf() {
    this.checkPos()
  }
}